import { btn } from "@/components/Page";

const formatDuration = (seconds) => {
  const safe = Math.max(0, Number(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const mins = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  if (hours > 0) return `${hours}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const rewardText = (rewards = []) => rewards.map((entry) => `${entry.min}–${entry.max} ${entry.rarity}`).join(" • ");
const actualRewardText = (rewards = {}) => Object.entries(rewards).filter(([, v]) => v > 0).map(([rarity, amount]) => `${amount} ${rarity}`).join(" • ");

export const MiningSessionPanel = ({ currentSession, pendingClaim, claimBusy, onClaim }) => {
  return (
    <section data-testid="mining-session-panel" className="border border-amber-900/30 bg-zinc-950/80 p-4 rounded-sm">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Current Session</h2>
          <p className="text-xs text-zinc-500 mt-1">Mining runs continue while you are offline.</p>
        </div>
      </div>

      {pendingClaim ? (
        <div data-testid="mining-pending-claim" className="space-y-4">
          <div className="border border-amber-700/40 bg-amber-950/10 p-4 rounded-sm">
            <div data-testid="mining-pending-claim-mine" className="font-cinzel text-lg text-zinc-100">{pendingClaim.mine_name}</div>
            <div className="mt-2 text-xs text-zinc-400">Completed and ready to claim.</div>
            <div data-testid="mining-pending-claim-rewards" className="mt-3 text-xs text-zinc-200">{actualRewardText(pendingClaim.raw_ore_rewards)}</div>
            <div data-testid="mining-pending-claim-xp" className="mt-1 text-xs text-amber-300">{pendingClaim.xp_gained} Mining XP</div>
            <div data-testid="mining-pending-claim-durability" className="mt-1 text-xs text-zinc-400">Tool durability after run: {pendingClaim.tool_durability_after}</div>
            {pendingClaim.great_yield_triggered && <div data-testid="mining-pending-claim-great-yield" className="mt-2 text-xs uppercase tracking-[0.2em] text-amber-400">Great yield discovered</div>}
            {pendingClaim.discovery && (
              <div data-testid="mining-pending-claim-discovery" className="mt-4 border border-amber-900/30 bg-zinc-950/50 p-3 rounded-sm">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-300">Rare discovery</div>
                <div className="font-cinzel text-base text-zinc-100 mt-2">{pendingClaim.discovery.name}</div>
                <div className="text-xs text-zinc-400 mt-1">{pendingClaim.discovery.summary}</div>
              </div>
            )}
          </div>
          <button data-testid="mining-claim-button" onClick={onClaim} disabled={claimBusy} className={btn() + " w-full justify-center"}>
            {claimBusy ? "Claiming" : "Claim Rewards"}
          </button>
        </div>
      ) : currentSession ? (
        <div data-testid="mining-active-session" className="space-y-4">
          <div className="border border-zinc-800 bg-zinc-900/70 p-4 rounded-sm space-y-2">
            <div data-testid="mining-active-mine-name" className="font-cinzel text-lg text-zinc-100">{currentSession.mine_name}</div>
            {currentSession.offline_auto && (
              <div data-testid="mining-active-offline-auto-badge" className="text-[11px] uppercase tracking-[0.22em] text-amber-300">
                Offline auto-mining expedition
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3 text-xs text-zinc-400">
              <div data-testid="mining-active-timer">Time remaining: <span className="text-amber-300">{formatDuration(currentSession.time_remaining_seconds)}</span></div>
              <div data-testid="mining-active-tool">Tool: <span className="text-zinc-100">{currentSession.tool_name}</span></div>
              <div data-testid="mining-active-energy-cost">Energy cost: <span className="text-zinc-100">{currentSession.energy_cost}</span></div>
              <div data-testid="mining-active-risk">Risk: <span className="text-zinc-100">{currentSession.risk_label}</span></div>
            </div>
          </div>
          <div className="text-xs text-zinc-300">
            <div data-testid="mining-active-expected-rewards">Expected rewards: {rewardText(currentSession.expected_ores)}</div>
            <div data-testid="mining-active-expected-xp" className="mt-1">XP: {currentSession.xp_min}–{currentSession.xp_max}</div>
            <div data-testid="mining-active-great-yield" className="mt-1">Great yield chance: {(currentSession.great_yield_chance * 100).toFixed(2)}%</div>
            <div data-testid="mining-active-discovery-chance" className="mt-1">Discovery chance: {((currentSession.rare_discovery_chance || 0) * 100).toFixed(2)}%</div>
            {currentSession.offline_auto && (
              <div data-testid="mining-active-offline-auto-rules" className="mt-2 text-amber-300/90">
                Offline rates: 50% XP and ore • 50% discovery chance • 65% less durability loss.
              </div>
            )}
          </div>
          {!!currentSession.possible_discoveries?.length && (
            <div data-testid="mining-active-discovery-list" className="border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Possible discoveries</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {currentSession.possible_discoveries.map((discovery) => (
                  <span key={discovery.id} data-testid={`mining-active-discovery-${discovery.id}`} className="border border-amber-900/30 bg-amber-950/10 px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-amber-300">
                    {discovery.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="mining-session-empty" className="border border-dashed border-amber-900/30 bg-zinc-950/40 p-6 text-center text-zinc-500">
          No mining session is active. Pick a location below to begin.
        </div>
      )}
    </section>
  );
};